import React from "react";
import { Box, Card, Typography } from "@mui/material";
import { ArrowDownwardRounded, ArrowUpwardRounded } from "@mui/icons-material";
import { formatCount, formatDelta } from "utils/chartsPalette";
import { T, cardSx, trendPillSx } from "utils/backrrTokens";
import Sparkline from "./Sparkline";
import InfoHint from "./InfoHint";

// One KPI card in startup-web's Performance language: an icon chip beside the
// label, the value, a trend pill, and a gradient sparkline on the right.
const StatTile = ({ label, metric, series = [], footnote, icon: Icon, color = T.accent, hint }) => {
  const isRate = metric?.unit === "%";

  // For a percentage, a relative change of a percentage invites a
  // percentage-point reading and misstates it: 41.2% -> 43% is +1.8pp, not
  // +4.4%. Show the point difference.
  const pointDelta =
    isRate &&
    metric?.value !== null && metric?.value !== undefined &&
    metric?.previous !== null && metric?.previous !== undefined
      ? Math.round((metric.value - metric.previous) * 10) / 10
      : null;

  const delta = isRate
    ? formatDelta(pointDelta, metric?.higher_is_better)
    : formatDelta(metric?.delta_pct, metric?.higher_is_better);

  // Previously an unknown delta rendered as a dash icon AND a dash, so a tile
  // with no prior period read "167 – –", which looks broken rather than empty.
  const hasDelta = delta.text !== "—";
  const deltaText = isRate && pointDelta !== null ? `${Math.abs(pointDelta)}pp` : delta.text;
  const Arrow = delta.direction === "down" ? ArrowDownwardRounded : ArrowUpwardRounded;

  const hasValue = metric?.value !== null && metric?.value !== undefined;
  const value = hasValue ? `${formatCount(metric.value)}${metric.unit ?? ""}` : "—";
  const exact = hasValue ? `${metric.value}${metric.unit ?? ""}` : "No data for this range";

  const plottable = series.filter((v) => typeof v === "number");
  const showSpark = plottable.length > 1 && plottable.some((v) => v > 0);
  return (
    // No height: 100% here. In a grid, a percentage height resolves against a
    // row that was itself sized from the content, and the two disagree: the row
    // came out 124px while the card's own content needs 154, so every tile
    // painted 30px outside its box — over the card in the row below, which is
    // why these tiles appeared to have no bottom border. The grid's default
    // stretch already equalises the four, and it does it correctly.
    <Card sx={{ ...cardSx, padding: "14px 16px" }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box display="flex" alignItems="center" gap={1} marginBottom={0.75}>
            {Icon && (
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  background: T.iconChip,
                  color: T.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  "& svg": { fontSize: 17 },
                }}
              >
                <Icon />
              </Box>
            )}
            <Typography
              sx={{
                // 0.78rem, the page's one label size — the breakdown rows and
                // the pipeline legend use it too. 0.8 and 0.78 side by side are
                // not a hierarchy, they are a rounding error.
                fontSize: "0.78rem",
                fontWeight: 500,
                color: T.inkMuted,
                // Wrap rather than ellipsize. A truncated metric name ("New
                // Invest…") is a label that no longer says what the number
                // means, and the info button beside it had squeezed exactly
                // that out of the tile. Two lines cost height the grid already
                // equalises; a cut-off word costs the reader the metric.
                lineHeight: 1.25,
              }}
            >
              {label}
            </Typography>
            {hint && <InfoHint text={hint} label={label} />}
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography
              title={exact}
              sx={{
                fontSize: "1.65rem",
                fontWeight: 700,
                lineHeight: 1.15,
                color: T.ink,
                // Tabular figures keep the four cards' numbers on one baseline
                // grid instead of drifting as digits change.
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {value}
            </Typography>
            {hasDelta && (
              <Box
                component="span"
                sx={trendPillSx(delta.direction)}
                title={`vs ${metric.previous}${metric.unit ?? ""} in the previous period`}
              >
                {delta.direction !== "flat" && (
                  <Arrow titleAccess={delta.direction === "up" ? "increase" : "decrease"} sx={{ fontSize: "0.8rem" }} />
                )}
                {deltaText}
              </Box>
            )}
          </Box>
        </Box>

        {showSpark && (
          <Box sx={{ flexShrink: 0, marginTop: "2px" }}>
            <Sparkline values={plottable} color={color} />
          </Box>
        )}
      </Box>

      {footnote && (
        <Typography
          sx={{ fontSize: "0.72rem", color: T.inkSubtle, marginTop: 1, fontVariantNumeric: "tabular-nums" }}
        >
          {footnote}
        </Typography>
      )}
    </Card>
  );
};

export default StatTile;
